import React from 'react';
import { useZunelo } from '../../context/ZuneloContext';
import { useAuth } from '../../auth';
import { useTheme } from '../../theme';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useCompactLayout } from '../../hooks/useCompactLayout';
import { AppSidebar } from './AppSidebar';
import { AppModals } from './AppModals';
import { MainWorkspaceHeader } from './MainWorkspaceHeader';
import { CompactOverlays } from './MainLayoutOverlays';
import {
  RightWorkspaceSidebarContent,
  RightWorkspaceSidebarDummies,
  RightWorkspaceSidebarProvider,
} from './RightWorkspaceSidebar';
import { buildSourceCounts, isInspectorSheetOpen } from './mainLayoutHelpers';
import { useMainLayoutChrome } from './useMainLayoutChrome';
import { renderActiveWorkspaceView } from './ActiveWorkspaceView';
import { AppErrorBoundary } from '../../ui/common/AppErrorBoundary';
import { useReleaseUpdate } from '../../hooks/useReleaseUpdate';
import { ChatAside } from '../chat/ChatAside';
import { InspectorAside } from '../inspector/InspectorAside';
import { parseDemoSources } from '../../services/clients';
import type { WorkspaceToolType } from '../modals/ToolDrawerModal';
import { ForegroundNotificationToast } from '../notifications/ForegroundNotificationToast';

function appClassName(isCompact: boolean): string {
  return isCompact ? 'z-app z-app--compact' : 'z-app';
}

function viewSectionStyle(view: string): React.CSSProperties {
  const isRadar = view === 'radar';
  return {
    flex: '1 1 auto',
    minWidth: 0,
    overflowY: isRadar ? 'hidden' : 'auto',
    padding: isRadar ? 0 : '24px 28px 40px',
    display: 'flex',
  };
}

function SidebarBackdrop({
  visible,
  onClose,
}: {
  visible: boolean;
  onClose: () => void;
}): React.ReactElement | null {
  if (!visible) return null;
  return (
    <button
      type="button"
      className="z-sidebar-backdrop"
      data-testid="sidebar-backdrop"
      aria-label="Cerrar navegación"
      onClick={onClose}
    />
  );
}

export const MainLayout: React.FC = () => {
  const {
    view,
    setView,
    selectItem,
    isContextSurfaceOpen,
    items,
    loadingSources,
    agents,
    reminders,
    notes,
    llmWiki,
    dailySent,
    setSearchTarget,
    isChatOpen,
    conversations,
    activeConversationId,
    selectConversation,
    startNewChat,
    deleteConversation,
    unseenNotificationCount,
  } = useZunelo();
  const { user } = useAuth();
  const { getActiveBackgroundStyle } = useTheme();
  const isCompact = useCompactLayout();
  const chrome = useMainLayoutChrome(isCompact, { setView, selectItem });

  const selectActiveTool = (tool: Exclude<WorkspaceToolType, null>) => {
    chrome.openTool(tool);
  };

  useKeyboardShortcuts({
    isSidebarExpanded: chrome.isSidebarExpanded,
    setIsSidebarExpanded: chrome.setIsSidebarExpanded,
    activeTool: chrome.activeTool === 'wiki' ? null : chrome.activeTool,
    setActiveTool: chrome.setActiveTool,
    isDrawerOpen: isCompact && chrome.isDrawerOpen,
    onCloseDrawer: chrome.closeDrawer,
    onNavigate: () => chrome.closeDrawer(),
    userId: user?.uid,
  });

  const counts = buildSourceCounts(
    items,
    agents,
    reminders,
    notes,
    llmWiki.length,
    unseenNotificationCount,
  );
  const inspectorSheetOpen = isInspectorSheetOpen(isCompact, isContextSurfaceOpen);
  const chatSheetOpen = isCompact && isChatOpen;
  const drawerBlocksMain = isCompact && chrome.isDrawerOpen;
  const activeBgStyle = getActiveBackgroundStyle(view);
  const releaseUpdate = useReleaseUpdate();

  return (
    <div className={appClassName(isCompact)} data-compact={isCompact ? 'true' : 'false'}>
      <SidebarBackdrop visible={drawerBlocksMain} onClose={chrome.closeDrawer} />

      <RightWorkspaceSidebarProvider
        isCompact={isCompact}
        renderSidebar={false}
        closeOnNavigate={view}
        closeOnLeftDrawerOpen={chrome.isDrawerOpen}
        fallback={
          !isCompact && view !== 'radar' && view !== 'wiki' && view !== 'notifications' ? (
            <>
              <ChatAside variant="docked" />
              <InspectorAside variant="docked" />
            </>
          ) : undefined
        }
      >
        <ForegroundNotificationToast />
        <div className="z-workspace-shell-row">
          <AppSidebar
            isCompact={isCompact}
            isSidebarExpanded={chrome.isSidebarExpanded}
            isDrawerOpen={chrome.isDrawerOpen}
            isSettingsOpen={chrome.isSettingsOpen}
            sidebarRef={chrome.sidebarRef}
            view={view}
            counts={counts}
            loadingSources={loadingSources}
            dailySent={dailySent}
            activeTool={chrome.activeTool}
            conversations={conversations}
            activeConversationId={activeConversationId}
            authUser={user}
            onToggleSidebar={() => chrome.setIsSidebarExpanded(!chrome.isSidebarExpanded)}
            onNavigateToView={chrome.navigateToView}
            onOpenTool={chrome.openTool}
            onNewChat={() => startNewChat()}
            onSelectConversation={(id) => void selectConversation(id)}
            onDeleteConversation={(id) => void deleteConversation(id)}
            onOpenSettings={chrome.openSettings}
            onOpenSearch={() => setSearchTarget('global')}
            onCloseDrawer={chrome.closeDrawer}
            releaseUpdate={releaseUpdate}
          />
          <RightWorkspaceSidebarDummies
            enabled={
              view !== 'notifications' &&
              typeof window !== 'undefined' &&
              parseDemoSources(window.location.search).length > 0
            }
          />
          <div className="z-workspace-main-column">
            <MainWorkspaceHeader
              view={view}
              isCompact={isCompact}
              isDrawerOpen={chrome.isDrawerOpen}
              inspectorSheetOpen={inspectorSheetOpen}
              chatSheetOpen={chatSheetOpen}
              onOpenDrawer={chrome.openDrawer}
              onOpenSearch={() => setSearchTarget('global')}
            />
            <div className="z-workspace-shell-body">
              <main
                className="z-main"
                aria-hidden={drawerBlocksMain ? true : undefined}
                {...(drawerBlocksMain ? ({ inert: '' } as React.HTMLAttributes<HTMLElement>) : {})}
                style={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'var(--z-bg)',
                  transition: 'background-color 0.25s ease, background-image 0.25s ease',
                  overflow: 'hidden',
                  ...activeBgStyle,
                }}
              >
                <div
                  style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', overflow: 'hidden' }}
                  aria-hidden={inspectorSheetOpen || chatSheetOpen ? true : undefined}
                  {...(inspectorSheetOpen || chatSheetOpen
                    ? ({ inert: '' } as React.HTMLAttributes<HTMLElement>)
                    : {})}
                >
                  <section className="z-view-section" style={viewSectionStyle(view)}>
                    <AppErrorBoundary key={view} variant="section">
                      {renderActiveWorkspaceView(view, selectActiveTool)}
                    </AppErrorBoundary>
                  </section>
                </div>
              </main>
              <RightWorkspaceSidebarContent />
            </div>
          </div>

          <CompactOverlays
            isCompact={isCompact}
            isChatOpen={isChatOpen}
            inspectorSheetOpen={inspectorSheetOpen}
            view={view}
            isDrawerOpen={chrome.isDrawerOpen}
            onSelectView={chrome.navigateToView}
          />

          <AppModals
            isSettingsOpen={chrome.isSettingsOpen}
            activeTool={chrome.activeTool}
            onCloseSettings={chrome.closeSettings}
            onCloseActiveTool={chrome.closeActiveTool}
            onSelectActiveTool={selectActiveTool}
          />
        </div>
      </RightWorkspaceSidebarProvider>
    </div>
  );
};
